import React, { useCallback, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { setApiForbiddenHandler } from "../api/configureApi";
import AccessDeniedScreen from "../componenets/common/AccessDeniedScreen";

/**
 * App-wide 403 handling.
 *
 * A 403 is never a transient action failure — it's a standing fact about who the caller is, and
 * it was previously handled per call site, where the 403 body's missing `errorCode` collapsed
 * into the generic "אופס, קרה שגיאה בפעולה" toast while the page kept rendering and its children
 * kept firing more doomed requests. One page fixing that doesn't help the other ~44 services.
 *
 * So the generated client's response interceptor reports every 403 here (see configureApi), and
 * the provider swaps the routed content for the access-denied screen. Because the interceptor is
 * global, this covers every page and every service without touching a single call site.
 *
 * Two deliberate scoping rules:
 *  - Navigating clears the flag. Otherwise one 403 would poison every subsequent page.
 *  - 401 is NOT handled here — that's "not logged in", which belongs to the logout/login path.
 */
interface AccessDeniedContextValue {
  denied: boolean;
  /** Clear the flag — for a page that handles its own 403 and wants the app-wide screen suppressed. */
  clearDenied: () => void;
}

const AccessDeniedContext = React.createContext<AccessDeniedContextValue>({
  denied: false,
  clearDenied: () => {},
});

export const useAccessDenied = () => React.useContext(AccessDeniedContext);

export const AccessDeniedProvider = ({ children }: { children: React.ReactNode }) => {
  const [denied, setDenied] = useState(false);
  const location = useLocation();

  // Register with the HTTP layer once. The interceptor is module-level and outlives React, so an
  // unmount must deregister or a stale setState would leak across a remount (and warn).
  useEffect(() => {
    setApiForbiddenHandler(() => setDenied(true));
    return () => setApiForbiddenHandler(null);
  }, []);

  // A 403 describes the page you're ON. Once the user navigates away — via either exit button or
  // the nav — the verdict no longer applies and the next page must get a clean slate.
  useEffect(() => {
    setDenied(false);
  }, [location.pathname]);

  const clearDenied = useCallback(() => setDenied(false), []);

  const value = useMemo(() => ({ denied, clearDenied }), [denied, clearDenied]);

  return (
    <AccessDeniedContext.Provider value={value}>
      {children}
    </AccessDeniedContext.Provider>
  );
};

/**
 * Renders the access-denied screen INSTEAD of its children once a 403 has been seen.
 *
 * Replacing rather than overlaying is the point: the children are the page whose requests just
 * got refused, and leaving them mounted is what produced the original bug — a half-drawn page
 * whose child sections each fired their own 403 and spun forever.
 */
export const AccessDeniedGate = ({ children }: { children: React.ReactNode }) => {
  const { denied } = useAccessDenied();
  return denied ? <AccessDeniedScreen /> : <>{children}</>;
};

export default AccessDeniedContext;
